import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ComposerApiService } from '@services/composer-api.service';
import { ContainerReloadService } from '@services/container-reload.service';
import { GlobalDataService } from '@services/global-data.service';
import { ToolbarEventService } from '@services/toolbar-event.service';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Params } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TabComponent } from '@mds/ui';
import { ComposerGridComponent } from '../composer-grid/composer-grid.component';
import { SidePanelComponent } from '../../../layers/components/side-panel/side-panel.component';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { DescriptionComponent } from '../description/description.component';
import { MenuService } from '@services/menu.service';
import { MattersmapService } from '@pages/mattermaps/components/mattermaps-edit/services/mattersmap.service';
import { ComposerDataService } from '@pages/mattermaps/components/mattermaps-edit/services/composer-data.service';
import {
  ActionItem,
  ActionsPanelComponent,
  FloatingActionsData
} from '@dialogs/actions-panel/actions-panel.component';
import {
  ComposerTab,
  ComposerTabsResponse,
  PaginatedComposerResponse
} from '../composer-grid/utils/composer-grid.models';

@Component({
  selector: 'app-definition',
  templateUrl: './definition.component.html',
  styleUrls: ['./definition.component.scss'],
  standalone: true,
  imports: [
    TabComponent,
    CommonModule,
    ComposerGridComponent,
    MatSidenavModule,
    SidePanelComponent,
    MdsIconsModule,
    DescriptionComponent,
    ActionsPanelComponent
  ],
  providers: [MattersmapService, ComposerDataService]
})
export class DefinitionComponent implements OnInit {
  icons = MdsIcon;
  tabs: any[] = [];
  composerData: any[] = [];
  selectedTab: any;
  productInfo: any[];
  module = 'mattermaps';
  gridLoading = false;
  paginationMeta: { page: number; limit: number; total_count: number; has_more: boolean };
  private usePaginatedApi = false;
  private composerTabs: ComposerTab[] = [];
  private tabsResponse: ComposerTabsResponse;
  private tabDataCache = new Map<string, { selectedTab: any; paginationMeta: typeof this.paginationMeta }>();
  private _data: any;
  @Input() set data(value: any) {
    this._data = value;
    this.init();
  }
  get data() {
    return this._data;
  }

  @ViewChild('drawer') drawer: MatDrawer;
  sidepanelData: any;
  selectedRow: any;
  softReload: string;
  @Output() selectedTabName = new EventEmitter<string>();
  @Input() productImage: string;
  @Input() isPage = true;
  @Input() isForecastScenario = false;
  private destroyRef = inject(DestroyRef);
  private menuService = inject(MenuService);
  private cdr = inject(ChangeDetectorRef);
  private composerDataService = inject(ComposerDataService);
  private composerApiService = inject(ComposerApiService);
  actionsPanelData: FloatingActionsData = { title: '', actions: [] };

  constructor(
    private containerReloadService: ContainerReloadService,
    private globalDataService: GlobalDataService,
    private toolbarEventService: ToolbarEventService,
    private route: ActivatedRoute,
    private mattersmapService: MattersmapService
  ) {}

  ngOnInit(): void {
    this.containerReloadService.reloadContainerData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isReload) => {
      if (isReload) {
        this.reloadData();
        this.containerReloadService.reloadContainerData.next(false);
      }
    });
    this.route.parent.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: Params) => {
      if (this.isPage) {
        this.data = {
          rid: params.get('rid') || '',
          ...this.route.snapshot.params,
          layer: params.get('layer'),
          hashParams: this.route.snapshot.fragment
        };
      }
    });

    this.route.url.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((segments) => {
      const currentPath = segments.map((s) => s.path).join('/');
      if (currentPath.includes('explorer')) {
        this.data.layer = 'explorer';
      }
    });
  }

  reloadData() {
    if (this.usePaginatedApi && this.selectedTab?._tabKey) {
      this.tabDataCache.delete(this.selectedTab._tabKey);
      this.loadTabData(this.selectedTab._tabKey);
      return;
    }
    this.composerApiService.showAll(this.data.rid).subscribe((res: any[]): void => {
      res = res.sort((a, b) => a.sortlevel - b.sortlevel);
      this.composerData = res;
      this.mapProductInfo();
      this.composerData.forEach((e) => {
        e.productInfo = this.productInfo;
        e.targetRid = this.data.rid;
      });
      const currentTabName = this.selectedTab?.name;
      const updatedTabData = this.composerData.find((e) => e.name === currentTabName);
      if (updatedTabData) {
        this.selectedTab = updatedTabData;
        this.selectedTab.layer = this.data.page;
        this.showTabData();
      }
      if (!this.selectedTab && this.composerData.length > 0) {
        this.selectedTab = this.composerData[0];
        this.selectedTab.layer = this.data.page;
        this.showTabData();
      }
    });
  }

  toggleSidePanel() {
    this.drawer?.toggle();
  }

  activeTabs(event: any) {
    if (!event) return;

    if (this.usePaginatedApi) {
      const tabMeta = this.composerTabs.find((t) => t.label === event.name);
      if (tabMeta) {
        const cached = this.tabDataCache.get(tabMeta.key);
        if (cached) {
          this.selectedTab = cached.selectedTab;
          this.paginationMeta = cached.paginationMeta;
          this.showTabData();
          this.cdr.markForCheck();
        } else {
          this.gridLoading = true;
          this.cdr.markForCheck();
          this.loadTabData(tabMeta.key);
        }
      }
      return;
    }

    const updatedTab = this.composerData.find((e) => e.name === event.name);
    if (updatedTab) {
      this.selectedTab = {
        ...updatedTab,
        productInfo: this.productInfo,
        targetRid: this.data.rid,
        layer: this.data.page
      };
      this.showTabData();
    }
  }

  getSelectedRow(event: any) {
    this.selectedRow = event;
    this.openSidePanelFromTable();
  }

  private init(): void {
    if (!this.data) return;
    this.tabDataCache.clear();
    this.composerDataService.getComposerTabs(this.data.rid).subscribe({
      next: (tabsResponse) => {
        this.tabsResponse = tabsResponse;
        this.composerTabs = tabsResponse.tabs;

        if (this.composerTabs.length === 0) {
          this.usePaginatedApi = false;
          this.getAllComposerData();
        } else {
          this.usePaginatedApi = true;
          this.initPaginatedTabs(tabsResponse);
        }
      },
      error: () => {
        this.usePaginatedApi = false;
        this.getAllComposerData();
      }
    });
  }

  private initPaginatedTabs(tabsResponse: ComposerTabsResponse): void {
    const { tabs, node_meta } = tabsResponse;
    this.productInfo = [
      {
        title: node_meta.caption ?? '',
        card_type_label: node_meta.type_label ?? '',
        type: node_meta.type,
        rid: node_meta.rid
      }
    ];

    this.tabs = tabs.map((tab) => ({
      name: tab.label,
      label: tab.label,
      _tabKey: tab.key
    }));

    const tabList = tabs.map((t) => t.label);
    this.globalDataService.setTabGroupData(tabList);

    const firstTabKey = this.resolveInitialTabKey(tabs);
    this.loadTabData(firstTabKey);
    this.cdr.markForCheck();
  }

  private resolveInitialTabKey(tabs: ComposerTab[]): string {
    if (this.softReload) {
      const match = tabs.find((t) => t.label === this.softReload);
      if (match) {
        this.toolbarEventService.updateTabManually.next(this.softReload);
        this.softReload = undefined;
        return match.key;
      }
    }
    if (this.data.hashParams) {
      const match = tabs.find((t) => t.label?.toLowerCase() === this.data.hashParams.toLowerCase());
      if (match) {
        this.toolbarEventService.updateTabManually.next(match.label);
        return match.key;
      }
    }
    return tabs[0].key;
  }

  private loadTabData(tabKey: string, page = 1, limit = 20): void {
    this.composerDataService.getComposerTabData(this.data.rid, tabKey, page, limit).subscribe(
      (response: PaginatedComposerResponse) => {
        this.paginationMeta = {
          page: response.page,
          limit: response.limit,
          total_count: response.total_count,
          has_more: response.has_more
        };

        const tabMeta = this.composerTabs.find((t) => t.key === tabKey);
        const tabItem = response.items[0];
        if (tabItem) {
          this.selectedTab = {
            ...tabItem,
            productInfo: this.productInfo,
            targetRid: this.data.rid,
            layer: this.data.page,
            _tabKey: tabKey,
            _paginationMeta: this.paginationMeta,
            _composerDataService: this.composerDataService
          };

        } else {
          this.selectedTab = {
            name: tabMeta?.label,
            isGrid: false,
            sections: [],
            productInfo: this.productInfo,
            targetRid: this.data.rid,
            layer: this.data.page,
            _tabKey: tabKey,
            _paginationMeta: this.paginationMeta,
            actions: {}
          };
        }
        this.tabDataCache.set(tabKey, {
          selectedTab: this.selectedTab,
          paginationMeta: this.paginationMeta
        });
        this.gridLoading = false;
        this.showTabData();
        this.cdr.markForCheck();
      }
    );
  }

  private getAllComposerData(): void {
    this.composerApiService.showAll(this.data.rid).subscribe((res: any[]): void => {
      res = res.sort((a, b) => a.sortlevel - b.sortlevel);
      this.composerData = res;
      this.createComposerTabData();
    });
  }

  private createComposerTabData(): void {
    if (!this.composerData.length) return;
    this.mapProductInfo();
    const allFeature = this.globalDataService.getFeatureArray();
    this.composerData.forEach((e) => {
      e.productInfo = this.productInfo;
      e.targetRid = this.data.rid;
    });
    const filteredTabs = this.composerData.filter((e) => e.name);
    const tabList = filteredTabs.map((e) => e.name);
    this.globalDataService.setTabGroupData(tabList);
    this.tabs = filteredTabs.map((data) => ({
      ...data,
      label: data.name
    }));

    if (this.softReload) {
      this.selectedTab = this.composerData.find((e) => e.name === this.softReload);
      this.toolbarEventService.updateTabManually.next(this.softReload);
      this.softReload = undefined;
    } else if (this.data.hashParams) {
      this.selectedTab = this.composerData.find((e) => e.name?.toLowerCase() === this.data.hashParams.toLowerCase());
      if (this.selectedTab) {
        this.toolbarEventService.updateTabManually.next(this.selectedTab.name);
      } else {
        // if url fragment is not found then show first tab
        this.selectedTab = this.composerData[0];
      }
    } else {
      this.selectedTab = this.composerData[0];
    }
    this.selectedTab.layer = this.data.page;
    this.showTabData();
    this.cdr.markForCheck();
  }

  private showTabData() {
    this.updateMenuActions();
    if (this.selectedTab.isGrid) {
      this.showGrid();
    } else {
      this.openSidePanel();
    }
  }

  private updateMenuActions() {
    const { actions } = this.selectedTab;
    if (this.selectedTab.name === 'Environmental Impacts' || this.selectedTab.name === 'Exemptions') {
      this.menuService.updateMenuItem('re_calculate', {
        visible: actions.edit && actions.delete
      });
    } else if (this.selectedTab.name === 'Products') {
      this.menuService.updateMenuItem('enrich', { visible: actions.edit });
    } else {
      this.menuService.updateMenuItems([{ enrich: { visible: false } }, { re_calculate: { visible: false } }]);
    }
    this.selectedTabName.emit(this.selectedTab.name);
  }

  private showGrid() {
    if (this.selectedTab?.lines.length > 0) {
      const obj = {
        row: this.selectedTab?.lines[0]
      };
      if (this.productInfo[0].type !== 'process') {
        this.selectedRow = obj;
        this.openSidePanelFromTable();
      }
    } else {
      this.drawer?.close();
    }
  }

  private openSidePanelFromTable() {
    let name = '';
    let type = '';
    let rid = '';
    this.selectedRow?.row.forEach((row) => {
      let key = row.key.replace('$', '.');
      key = key.split('.');
      key = key.pop();
      if (['in', 'out', 'name'].includes(key)) {
        if (row.display_value) name = row.display_value;
        if (row.label) type = row.label;
        if (row.rid) rid = row.rid;
      }
    });
    this.mattersmapService
      .getSidePanelDetailsWithSubModule(rid, this.data.layer, 'composer', this.selectedTab.name)
      .subscribe((sidePanel) => {
        this.sidepanelData = {
          ...sidePanel,
          contextualTools: sidePanel.contextualTools.map((b) => ({
            ...b,
            lines: b.lines?.map((c) => ({ ...c, enableFullWidth: true })),
            list: b.list?.map((c) => ({ ...c, enableFullWidth: true }))
          }))
        };

        this.productInfo = [
          {
            title: name ?? '',
            card_type_label: type ?? ''
          }
        ];
        this.drawer?.open();
      });
  }

  private openSidePanel(): void {
    this.mattersmapService.getModuleTools(this.data.rid).subscribe((sidePanel: any) => {
      this.sidepanelData = {
        ...sidePanel,
        contextualTools: sidePanel.contextualTools.map((b) => ({
          ...b,
          lines: b.lines?.map((c) => ({ ...c, enableFullWidth: true })),
          list: b.list?.map((c) => ({ ...c, enableFullWidth: true }))
        }))
      };
      this.mapProductInfo();
    });
  }

  private mapProductInfo() {
    if (this.usePaginatedApi && this.tabsResponse?.node_meta) {
      const meta = this.tabsResponse.node_meta;
      this.productInfo = [
        {
          title: meta.caption ?? '',
          card_type_label: meta.type_label ?? '',
          type: meta.type,
          rid: meta.rid
        }
      ];
      return;
    }
    const product = this.composerData[this.composerData.length - 1];
    this.productInfo = [
      {
        title: product?.caption ?? '',
        card_type_label: product?.type_label ?? '',
        ...product
      }
    ];
  }

  updateFloatingAction(actions: ActionItem[]) {
    this.actionsPanelData.actions = actions;
  }
}
